import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import EncyclopediaSidebar from "@/components/EncyclopediaSidebar";
import { LabelBadge } from "@/components/LabelBadge";
import { ArcButton } from "@/components/ArcButton";
import { ArrowRight } from "lucide-react";
import { Editable, ImageBlockZone } from "@/components/Editable";

export default function ZyfleyHome() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles", { params: { encyclopedia: "zyfley" } }).then((r) => setArticles(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const main = articles.find((a) => a.slug === "zyfley");

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <header className="border-b pb-6">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--zyfley)" }}>The Archives</p>
        <Editable id="zyfley.header" as="h1" fallback="THE ZYFLEY ENCYCLOPEDIA" className="mt-2 font-serif-arc text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl" style={{ color: "var(--zyfley)" }} />
        <Editable id="zyfley.subtitle" as="p" fallback={'"An objective historical record of Zyfley and related events."'} className="mt-3 text-base italic" style={{ color: "var(--arc-text-2)" }} />
        <Editable id="zyfley.disclaimer" as="p" mode="multiline" fallback="This is an unofficial fan-made encyclopedia. Community jokes and fictional lore are clearly marked as satire." className="mt-4 max-w-3xl text-sm leading-relaxed" style={{ color: "var(--arc-text-2)" }} />
      </header>

      <div className="mt-8 flex flex-col gap-8 lg:flex-row">
        <EncyclopediaSidebar enc="zyfley" />

        <div className="min-w-0 flex-1">
          {main && (
            <section
              data-testid="zyfley-featured-article"
              className="mb-6 overflow-hidden rounded-md border-2 p-6 shadow-sm sm:p-8"
              style={{ backgroundColor: "var(--arc-card)", borderColor: "var(--zyfley)" }}
            >
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--zyfley)" }}>Featured Article</p>
              <h2 className="mt-2 font-serif-arc text-2xl font-black sm:text-3xl">Zyfley</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: "var(--arc-text-2)" }}>{main.description}</p>
              <div className="mt-6">
                <ArcButton to="/article/zyfley?enc=zyfley" variant="zyfley" className="w-full sm:w-auto">READ ARTICLE <ArrowRight size={16} /></ArcButton>
              </div>
            </section>
          )}

          <h3 className="font-serif-arc text-lg font-bold border-b pb-2">Contents of this encyclopedia</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {articles.map((a) => (
              <Link
                key={a.slug}
                to={`/article/${a.slug}?enc=zyfley`}
                data-testid={`zyfley-article-${a.slug}`}
                className="block rounded-md border p-5 shadow-sm transition-shadow hover:shadow-md"
                style={{ backgroundColor: "var(--arc-card)" }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-serif-arc font-bold zyfley-accent">{a.title}</span>
                  {a.badge && <LabelBadge label={a.badge} />}
                </div>
                <p className="mt-2 line-clamp-2 text-sm" style={{ color: "var(--arc-text-2)" }}>{a.description}</p>
              </Link>
            ))}
          </div>

          <div className="mt-6">
            <ArcButton to="/zyfley/all" variant="outline" data-testid="zyfley-view-more" className="w-full justify-center py-3">
              View More Articles <ArrowRight size={16} />
            </ArcButton>
          </div>

          <p className="mt-10 text-center text-xs italic" style={{ color: "var(--arc-text-2)" }}>
            [citation needed]
          </p>

          <ImageBlockZone id="zyfley.blocks" className="mt-8" />
        </div>
      </div>
    </div>
  );
}
